import {
  updateDoc, deleteDoc, getDocs, writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  docRef, studentDoc, studentSubCol, studentSubDoc,
} from '../../firebase/config';
import { FIRESTORE_BATCH_LIMIT } from '../../utils/firestoreLimits';
import { addProgram, buildProgramPayload } from '../../services/programs';
import { addSwimmingRecord as addSwimmingRecordService } from '../../services/swimmingRecords';
import { addStudentByInstructor } from '../../services/students';
import { submitInstructorRating } from '../../services/ratings';
import { sendNotification } from '../../services/notifications';
import { markNotificationRead } from '../../services/notifications';
import { studentToInstructorContext } from '../../utils/notificationContext';
import {
  extractIdentityValues,
  syncIdentities,
  describeIdentityError,
} from '../../services/identityUniqueness';

/**
 * Öğrenci verisi CRUD ve program işlemleri.
 * SRP: Yalnızca veri yazma/silme aksiyonları.
 */
export const useOgrenciIslemleri = ({
  loggedInUser,
  setLoggedInUser,
  students,
  showToast,
}) => {
  const notify = async (email, text, context = null) => {
    try {
      await sendNotification(email, text, context ? { context } : undefined);
    } catch (e) {
      console.warn('Bildirim gönderilemedi:', e);
    }
  };

  const notifyInstructor = async (text) => {
    const email = loggedInUser?.emailOrPhone;
    if (!email) return;
    await notify(email, text);
  };

  const updateStudentData = async (studentId, newData) => {
    try {
      const { userCode: _uc, role: _role, id: _id, ...safeData } = newData || {};
      const touchesIdentity = 'emailOrPhone' in safeData || 'phoneNumber' in safeData;
      if (touchesIdentity) {
        const current =
          (loggedInUser?.id === studentId ? loggedInUser : null) ||
          students.find((s) => s.id === studentId) ||
          {};
        const before = extractIdentityValues(current);
        const after = extractIdentityValues({ ...current, ...safeData });
        await syncIdentities(studentId, {
          claim: after,
          release: before.filter((v) => !after.includes(v)),
        });
      }
      await updateDoc(studentDoc(studentId), safeData);
      if (loggedInUser && loggedInUser.id === studentId)
        setLoggedInUser((prev) => ({ ...prev, ...safeData }));
      return true;
    } catch (err) {
      console.error(err);
      showToast(
        describeIdentityError(err) ||
          'Güncelleme kaydedilemedi. Lütfen tekrar deneyin.',
        'error'
      );
      return false;
    }
  };

  const updateInstructorData = async (instructorId, newData) => {
    try {
      const { userCode: _uc, role: _role, id: _id, ...safeData } = newData || {};
      const touchesIdentity = 'emailOrPhone' in safeData || 'phoneNumber' in safeData;
      if (touchesIdentity) {
        const current = loggedInUser?.id === instructorId ? loggedInUser : {};
        const before = extractIdentityValues(current);
        const after = extractIdentityValues({ ...current, ...safeData });
        await syncIdentities(instructorId, {
          claim: after,
          release: before.filter((v) => !after.includes(v)),
        });
      }
      await updateDoc(docRef('instructors', instructorId), safeData);
      if (loggedInUser && loggedInUser.id === instructorId)
        setLoggedInUser((prev) => ({ ...prev, ...safeData }));
      return true;
    } catch (err) {
      console.error(err);
      showToast(describeIdentityError(err) || 'Güncelleme başarısız oldu!', 'error');
      return false;
    }
  };

  const createProgram = async (student, { dateISO, warmup, main, cooldown }) => {
    const payload = buildProgramPayload({ dateISO, warmup, main, cooldown });
    await addProgram(student.id, payload);
    const name = student?.name || 'Öğrenci';
    await notifyInstructor(`${name} için yeni bir antrenman görevi atandı.`);
    return payload;
  };

  const createSwimmingRecord = async (student, record) => {
    await addSwimmingRecordService(student.id, record);
    const name = student?.name || 'Öğrenci';
    await notifyInstructor(`${name} için yeni yüzme derecesi eklendi.`);
  };

  const createStudentByInstructor = async (instructor, formData) => {
    await addStudentByInstructor(instructor, formData);
    await notifyInstructor(`${formData.name} sisteme eklendi.`);
  };

  const submitRating = async (instructorEmail, rating) => {
    if (!loggedInUser?.id) throw new Error('Oturum bulunamadı.');
    await submitInstructorRating(loggedInUser.id, instructorEmail, rating);
  };

  const markNotifRead = async (notificationId) => {
    await markNotificationRead(notificationId);
  };

  const archiveStudentPrograms = async (studentId) => {
    const snap = await getDocs(studentSubCol(studentId, 'programs'));
    const toArchive = snap.docs.filter((d) => !d.data().archived);
    if (toArchive.length === 0) return;

    try {
      for (let i = 0; i < toArchive.length; i += FIRESTORE_BATCH_LIMIT) {
        const batch = writeBatch(db);
        const chunk = toArchive.slice(i, i + FIRESTORE_BATCH_LIMIT);
        chunk.forEach((d) => {
          batch.update(d.ref, { archived: true, archivedAt: Date.now() });
        });
        await batch.commit();
      }
    } catch (e) {
      showToast('Programlar arşivlenirken hata oluştu.', 'error');
      throw e;
    }
  };

  const handleDeleteProgram = async (studentId, programId) => {
    const student = students.find((s) => s.id === studentId);
    try {
      await updateDoc(studentSubDoc(studentId, 'programs', programId), {
        archived: true,
        archivedAt: Date.now(),
      });
      if (student) {
        await notifyInstructor(
          `${student.name} için bir antrenman görevi arşive taşındı.`
        );
      }
    } catch (e) {
      showToast('Program arşivlenemedi.', 'error');
    }
  };

  const handleDeleteSwimmingRecord = async (studentId, recordId) => {
    const student = students.find((s) => s.id === studentId);
    try {
      await deleteDoc(studentSubDoc(studentId, 'swimmingRecords', recordId));
      if (student) {
        await notifyInstructor(`${student.name} için bir yüzme derecesi silindi.`);
      }
      showToast('Derece silindi.');
    } catch (e) {
      showToast('Derece silinemedi.', 'error');
    }
  };

  const handleDeleteProgramPermanent = async (studentId, programId) => {
    const student = students.find((s) => s.id === studentId);
    try {
      await deleteDoc(studentSubDoc(studentId, 'programs', programId));
      if (student) {
        await notifyInstructor(`${student.name} için bir antrenman görevi silindi.`);
      }
      showToast('Görev silindi.');
    } catch (e) {
      showToast('Görev silinemedi.', 'error');
    }
  };

  const handleMarkWorkoutDone = async (workoutId) => {
    if (!loggedInUser) return;
    try {
      await updateDoc(studentSubDoc(loggedInUser.id, 'programs', workoutId), {
        completed: true,
      });
      if (loggedInUser.instructorEmail) {
        await notify(
          loggedInUser.instructorEmail,
          `Öğrenciniz ${loggedInUser.name} bireysel bir antrenman görevini başarıyla tamamladı!`,
          studentToInstructorContext(loggedInUser.id, loggedInUser.instructorId)
        );
      }
      showToast('Tebrikler! Antrenman görevi tamamlandı.');
    } catch (err) {
      showToast('İşlem sırasında hata oluştu: ' + err.message, 'error');
    }
  };

  return {
    updateStudentData,
    updateInstructorData,
    createProgram,
    createSwimmingRecord,
    createStudentByInstructor,
    submitRating,
    markNotifRead,
    archiveStudentPrograms,
    handleDeleteProgram,
    handleDeleteSwimmingRecord,
    handleDeleteProgramPermanent,
    handleMarkWorkoutDone,
    sendNotification: notify,
  };
};
